(ns frontend.view.settings2.connected-plugins.events
  "Re-frame event handlers for the Connected Plugins settings UI"
  (:require
   [frontend.controller.websocket.event :as ws]
   [frontend.logging :as logging]
   [frontend.view.settings2.connected-plugins.sub :as sub]
   [re-frame.core :as rf]))

;;
;; Polling interval calculation
;;

(defn- get-poll-interval
  "Get the polling interval based on elapsed time since code generation.
   - First 30 seconds: every 2 seconds
   - 30s to 2 minutes: every 5 seconds
   - After 2 minutes: every 10 seconds"
  [elapsed-ms]
  (cond
    (< elapsed-ms 30000)  2000   ; First 30s: every 2s
    (< elapsed-ms 120000) 5000   ; 30s-2min: every 5s
    :else                 10000)) ; After 2min: every 10s

;;
;; Initialize state
;;

(rf/reg-event-fx
 ::init
 (fn [{db :db} _]
   {:db (assoc-in db sub/root-path
                  {:loading-state :loading
                   :connections []
                   :generating? false
                   :revoking? false
                   :read-only? false
                   :plugin-codes {}
                   :pending-plugin-type nil
                   :revoke-error nil
                   :connection-name ""
                   :connection-name-loaded? false
                   :plugin-type :plugin.type/outlook
                   :connection-lifetime nil
                   :tenant-max-lifetime nil
                   :tenant-mcp-max-lifetime nil
                   :polling? false
                   :poll-timeout-id nil
                   :initial-connection-count nil
                   :connection-established? false})
    :fx [[:dispatch [::load-connections]]
         [:dispatch [::load-latest-connection-name]]
         [:dispatch [::load-tenant-plugin-settings]]]}))

;;
;; Load tenant plugin settings (max connection lifetime)
;;

(rf/reg-event-fx
 ::load-tenant-plugin-settings
 (fn [_ _]
   {::ws-load-tenant-plugin-settings {}}))

(rf/reg-fx
 ::ws-load-tenant-plugin-settings
 (fn [_]
   (ws/ws-send!
    {:id         :plugin/get-tenant-plugin-settings
     :body       {}
     :on-success [::on-tenant-plugin-settings-loaded]
     :on-error   [::on-tenant-plugin-settings-error]})))

(rf/reg-event-fx
 ::on-tenant-plugin-settings-loaded
 (fn [{db :db} [_ resp]]
   (let [max-lifetime (-> resp :body :max-connection-lifetime)
         mcp-max-lifetime (-> resp :body :mcp-max-connection-lifetime)
         mcp-url (-> resp :body :mcp-url)]
     {:db (-> db
              (assoc-in (conj sub/root-path :tenant-max-lifetime) max-lifetime)
              (assoc-in (conj sub/root-path :tenant-mcp-max-lifetime) mcp-max-lifetime)
              (assoc-in (conj sub/root-path :mcp-url) mcp-url)
              ;; Default user selection to tenant max
              (assoc-in (conj sub/root-path :connection-lifetime) max-lifetime))})))

(rf/reg-event-fx
 ::on-tenant-plugin-settings-error
 (fn [_ [_ err]]
   (logging/error "Failed to load tenant plugin settings" err)
   {}))

;;
;; Load latest connection name for prefill
;;

(rf/reg-event-fx
 ::load-latest-connection-name
 (fn [_ _]
   {::ws-load-latest-connection-name {}}))

(rf/reg-fx
 ::ws-load-latest-connection-name
 (fn [_]
   (ws/ws-send!
    {:id         :plugin/get-latest-connection-name
     :body       {}
     :on-success [::on-latest-connection-name-loaded]
     :on-error   [::on-latest-connection-name-error]})))

(rf/reg-event-fx
 ::on-latest-connection-name-loaded
 (fn [{db :db} [_ resp]]
   (let [connection-name (-> resp :body :token/plugin-connection-name)
         current-name (get-in db (conj sub/root-path :connection-name))]
     ;; Only prefill if user hasn't already typed something
     {:db (cond-> (assoc-in db (conj sub/root-path :connection-name-loaded?) true)
            (and connection-name (empty? current-name))
            (assoc-in (conj sub/root-path :connection-name) connection-name))})))

(rf/reg-event-fx
 ::on-latest-connection-name-error
 (fn [{db :db} [_ err]]
   (logging/error "Failed to load latest connection name" err)
   {:db (assoc-in db (conj sub/root-path :connection-name-loaded?) true)}))

(rf/reg-fx
 ::clear-timeout
 (fn [timeout-id]
   (when timeout-id
     (js/clearTimeout timeout-id))))

;; Cleanup state when leaving the view
(rf/reg-event-fx
 ::cleanup
 (fn [{db :db} _]
   (let [timeout-id (get-in db (conj sub/root-path :poll-timeout-id))]
     (cond-> {:db (update db ::sub/state (constantly nil))}
       timeout-id (assoc ::clear-timeout timeout-id)))))

;; Update connection name input
(rf/reg-event-fx
 ::set-connection-name
 (fn [{db :db} [_ connection-name]]
   {:db (assoc-in db (conj sub/root-path :connection-name) connection-name)}))

;; Update connection lifetime selection
(rf/reg-event-fx
 ::set-connection-lifetime
 (fn [{db :db} [_ lifetime]]
   {:db (assoc-in db (conj sub/root-path :connection-lifetime) lifetime)}))

;; Toggle read-only scope for MCP connections
(rf/reg-event-fx
 ::set-read-only?
 (fn [{db :db} [_ read-only?]]
   {:db (assoc-in db (conj sub/root-path :read-only?) read-only?)}))

;;
;; Load connections
;;

(rf/reg-event-fx
 ::load-connections
 (fn [_ _]
   {::ws-load-connections {}}))

(rf/reg-fx
 ::ws-load-connections
 (fn [_]
   (ws/ws-send!
    {:id         :plugin/list-connections
     :body       {}
     :on-success [::on-connections-loaded]
     :on-error   [::on-connections-load-error]})))

(rf/reg-event-fx
 ::on-connections-loaded
 (fn [{db :db} [_ resp]]
   (let [connections (-> resp :body :connections)
         initial-count (get-in db (conj sub/root-path :initial-connection-count))
         polling? (get-in db (conj sub/root-path :polling?))
         ;; Check if a new connection was established while polling
         connection-established? (and polling?
                                      (some? initial-count)
                                      (> (count connections) initial-count))]
     (cond-> {:db (-> db
                      (assoc-in (conj sub/root-path :loading-state) :loaded)
                      (assoc-in (conj sub/root-path :connections) connections))}
       ;; If connection established, stop polling and clear the pairing code
       connection-established?
       (-> (update :db #(-> %
                            (assoc-in (conj sub/root-path :connection-established?) true)
                            (assoc-in (conj sub/root-path :polling?) false)))
           (assoc :fx [[:dispatch [::stop-polling]]
                       [:dispatch-later [{:ms 2000 :dispatch [::clear-connection-established-flag]}]]]))))))

(rf/reg-event-fx
 ::clear-connection-established-flag
 (fn [{db :db} _]
   (let [plugin-type (get-in db (conj sub/root-path :pending-plugin-type))]
     {:db (-> db
              (assoc-in (conj sub/root-path :connection-established?) false)
              (assoc-in (conj sub/root-path :plugin-codes plugin-type) {})
              (assoc-in (conj sub/root-path :initial-connection-count) nil)
              (assoc-in (conj sub/root-path :connection-name) ""))})))

(rf/reg-event-fx
 ::on-connections-load-error
 (fn [{db :db} [_ err]]
   (logging/error "Failed to load connections" err)
   {:db (-> db
            (assoc-in (conj sub/root-path :loading-state) :error)
            (assoc-in (conj sub/root-path :error) (or (:message err) "Failed to load connections")))}))

;;
;; Generate pairing code
;;

(rf/reg-event-fx
 ::generate-pairing-code
 (fn [{db :db} [_ connection-name plugin-type connection-lifetime read-only?]]
   ;; Store current connection count before generating code
   (let [current-connection-count (count (get-in db (conj sub/root-path :connections)))]
     {:db (-> db
              (assoc-in (conj sub/root-path :generating?) true)
              (assoc-in (conj sub/root-path :pending-plugin-type) plugin-type)
              (assoc-in (conj sub/root-path :plugin-codes plugin-type :pairing-error) nil)
              (assoc-in (conj sub/root-path :initial-connection-count) current-connection-count)
              (assoc-in (conj sub/root-path :connection-established?) false))
      ::ws-generate-pairing-code {:connection-name     connection-name
                                  :plugin-type         plugin-type
                                  :connection-lifetime connection-lifetime
                                  :read-only?          read-only?}})))

(rf/reg-fx
 ::ws-generate-pairing-code
 (fn [{:keys [connection-name plugin-type connection-lifetime read-only?]}]
   (ws/ws-send!
    {:id         :plugin/generate-pairing-code
     :body       (cond-> {:connection-name connection-name
                          :plugin-type     plugin-type}
                   connection-lifetime
                   (assoc :connection-lifetime connection-lifetime)
                   (and (= plugin-type :plugin.type/mcp) read-only?)
                   (assoc :read-only? true))
     :on-success [::on-pairing-code-generated]
     :on-error   [::on-pairing-code-error]})))

(rf/reg-event-fx
 ::on-pairing-code-generated
 (fn [{db :db} [_ resp]]
   (let [{:keys [pairing-code expires]} (:body resp)
         plugin-type (get-in db (conj sub/root-path :pending-plugin-type))
         now (js/Date.now)]
     {:db (-> db
              (assoc-in (conj sub/root-path :generating?) false)
              (assoc-in (conj sub/root-path :plugin-codes plugin-type :pairing-code) pairing-code)
              (assoc-in (conj sub/root-path :plugin-codes plugin-type :pairing-code-expires) expires)
              (assoc-in (conj sub/root-path :plugin-codes plugin-type :pairing-code-generated-at) now)
              (assoc-in (conj sub/root-path :polling?) true))
      :fx [[:dispatch [::start-polling]]]})))

(rf/reg-event-fx
 ::on-pairing-code-error
 (fn [{db :db} [_ err]]
   (logging/error "Failed to generate pairing code" err)
   (let [plugin-type (get-in db (conj sub/root-path :pending-plugin-type))]
     {:db (-> db
              (assoc-in (conj sub/root-path :generating?) false)
              (assoc-in (conj sub/root-path :plugin-codes plugin-type :pairing-error)
                        (or (:message err) "Failed to generate pairing code")))})))

;;
;; Clear pairing code
;;

(rf/reg-event-fx
 ::clear-pairing-code
 (fn [{db :db} [_ plugin-type]]
   {:db (-> db
            (assoc-in (conj sub/root-path :plugin-codes plugin-type) {})
            (assoc-in (conj sub/root-path :polling?) false)
            (assoc-in (conj sub/root-path :initial-connection-count) nil)
            (assoc-in (conj sub/root-path :connection-established?) false))
    :fx [[:dispatch [::stop-polling]]]}))

;;
;; Polling for connection
;;

(rf/reg-event-fx
 ::start-polling
 (fn [{db :db} _]
   (let [plugin-type (get-in db (conj sub/root-path :pending-plugin-type))
         generated-at (get-in db (conj sub/root-path :plugin-codes plugin-type :pairing-code-generated-at))
         expires (get-in db (conj sub/root-path :plugin-codes plugin-type :pairing-code-expires))
         now (js/Date.now)
         elapsed (- now generated-at)
         interval (get-poll-interval elapsed)]
     ;; Only start polling if code hasn't expired
     (if (and expires (< now expires))
       {::schedule-poll {:interval interval}}
       {:fx [[:dispatch [::stop-polling]]]}))))

(rf/reg-fx
 ::schedule-poll
 (fn [{:keys [interval]}]
   (let [timeout-id (js/setTimeout
                     #(rf/dispatch [::poll-for-connection])
                     interval)]
     ;; Store the timeout ID so we can cancel it later
     (rf/dispatch [::set-poll-timeout-id timeout-id]))))

(rf/reg-event-fx
 ::set-poll-timeout-id
 (fn [{db :db} [_ timeout-id]]
   {:db (assoc-in db (conj sub/root-path :poll-timeout-id) timeout-id)}))

(rf/reg-event-fx
 ::poll-for-connection
 (fn [{db :db} _]
   (let [polling? (get-in db (conj sub/root-path :polling?))
         plugin-type (get-in db (conj sub/root-path :pending-plugin-type))
         expires (get-in db (conj sub/root-path :plugin-codes plugin-type :pairing-code-expires))
         now (js/Date.now)]
     (if (and polling? expires (< now expires))
       ;; Continue polling - load connections and schedule next poll
       {:fx [[:dispatch [::load-connections]]
             [:dispatch [::start-polling]]]}
       ;; Stop polling - code expired or polling disabled
       {:db (assoc-in db (conj sub/root-path :polling?) false)}))))

(rf/reg-event-fx
 ::stop-polling
 (fn [{db :db} _]
   (let [timeout-id (get-in db (conj sub/root-path :poll-timeout-id))]
     (cond-> {:db (-> db
                      (assoc-in (conj sub/root-path :polling?) false)
                      (assoc-in (conj sub/root-path :poll-timeout-id) nil))}
       timeout-id (assoc ::clear-timeout timeout-id)))))

;;
;; Revoke connection
;;

(rf/reg-event-fx
 ::revoke-connection
 (fn [{db :db} [_ connection-id]]
   {:db (-> db
            (assoc-in (conj sub/root-path :revoking?) true)
            (assoc-in (conj sub/root-path :revoke-error) nil))
    ::ws-revoke-connection {:connection-id connection-id}}))

(rf/reg-fx
 ::ws-revoke-connection
 (fn [{:keys [connection-id]}]
   (ws/ws-send!
    {:id         :plugin/revoke-connection
     :body       {:connection-id connection-id}
     :on-success [::on-connection-revoked]
     :on-error   [::on-revoke-error]})))

(rf/reg-event-fx
 ::on-connection-revoked
 (fn [{db :db} _]
   {:db (assoc-in db (conj sub/root-path :revoking?) false)
    :fx [[:dispatch [::load-connections]]]}))

(rf/reg-event-fx
 ::on-revoke-error
 (fn [{db :db} [_ err]]
   (logging/error "Failed to revoke connection" err)
   {:db (-> db
            (assoc-in (conj sub/root-path :revoking?) false)
            (assoc-in (conj sub/root-path :revoke-error)
                      (or (:message err) "Failed to revoke connection")))}))
